import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CountryPhoneField } from './CountryPhoneField';
import { OutreachPlatformField } from './OutreachPlatformField';
import { OnboardingPillGroup } from './OnboardingPillGroup';
import { ArrowRight, ChevronLeft } from '@/icons/Icon';
import { FIELD_MENU_CLASS } from '@/lib/field-skin';
import { detectDefaultCountry } from './country-phone-options';
import { validatePhoneForCountry } from './validate-phone';
import {
  COMPANY_SIZE_OPTIONS,
  INDUSTRY_OPTIONS,
  MAILBOX_COUNT_OPTIONS,
  MONTHLY_EMAIL_VOLUME_OPTIONS,
  OUTREACH_PLATFORM_OPTIONS,
  REFERRAL_SOURCE_OPTIONS,
  type OnboardingFormInput,
} from './schemas';

type Props = {
  onSubmit: (payload: OnboardingFormInput) => void;
  submitting: boolean;
  error: string | null;
};

const STEP_COUNT = 2;

export function OnboardingForm({ onSubmit, submitting, error }: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [industry, setIndustry] = useState<string>('');
  const [companySize, setCompanySize] = useState<string>('');
  const [mailboxCount, setMailboxCount] = useState<string>('');
  const [monthlyEmailVolume, setMonthlyEmailVolume] = useState<string>('');
  const [outreachPlatform, setOutreachPlatform] = useState<string>('');
  const [referralSource, setReferralSource] = useState<string>('');
  const [defaultCountry] = useState(() => detectDefaultCountry());
  const [countryCode, setCountryCode] = useState<string>(defaultCountry.dialCode);
  const [countryIso2, setCountryIso2] = useState<string>(defaultCountry.iso2);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [showPhoneError, setShowPhoneError] = useState(false);

  const phoneValidation = useMemo(
    () => validatePhoneForCountry(phoneNumber, countryIso2),
    [phoneNumber, countryIso2],
  );

  const step1Done =
    companySize !== '' &&
    mailboxCount !== '' &&
    monthlyEmailVolume !== '' &&
    phoneValidation.valid;
  const step2Done = industry !== '' && outreachPlatform !== '' && referralSource.trim() !== '';
  const canSubmit = step1Done && step2Done;

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    if (step === 1) {
      if (step1Done) setStep(2);
      else if (!phoneValidation.valid) setShowPhoneError(true);
      return;
    }
    if (!canSubmit) return;

    onSubmit({
      industry,
      companySize,
      mailboxCount,
      monthlyEmailVolume,
      outreachPlatform: outreachPlatform.trim(),
      referralSource: referralSource.trim(),
      phoneNumber: phoneNumber.trim(),
      countryCode,
    });
  }

  return (
    <form className="onboarding-form" onSubmit={handleSubmit} noValidate>
      <div className="onboarding-progress">
        <div
          className="onboarding-progress-track"
          role="progressbar"
          aria-label="Onboarding steps"
          aria-valuemin={1}
          aria-valuemax={STEP_COUNT}
          aria-valuenow={step}
        >
          <div
            className="onboarding-progress-fill"
            style={{ width: `${(step / STEP_COUNT) * 100}%` }}
          />
        </div>
        <span className="onboarding-progress-label num">
          Step {step} of {STEP_COUNT}
        </span>
      </div>

      <div className="onboarding-body">
        {error && (
          <p
            className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3"
            role="alert"
          >
            {error}
          </p>
        )}

        {step === 1 ? (
          <>
            <OnboardingPillGroup
              label="What is your company size?"
              name="company-size"
              options={COMPANY_SIZE_OPTIONS}
              value={companySize}
              onChange={setCompanySize}
            />

            <OnboardingPillGroup
              label="How many mailboxes do you want to warm up monthly?"
              name="mailbox-count"
              options={MAILBOX_COUNT_OPTIONS}
              value={mailboxCount}
              onChange={setMailboxCount}
            />

            <OnboardingPillGroup
              label="How many emails are you planning to send monthly?"
              name="monthly-email-volume"
              options={MONTHLY_EMAIL_VOLUME_OPTIONS}
              value={monthlyEmailVolume}
              onChange={setMonthlyEmailVolume}
            />

            <div className="onboarding-field">
              <Label htmlFor="phone-number" className={FIELD_LABEL_CLASS}>
                Phone number
              </Label>
              <CountryPhoneField
                phone={phoneNumber}
                countryCode={countryCode}
                countryIso2={countryIso2}
                onPhoneChange={setPhoneNumber}
                onCountryChange={(dialCode, iso2) => {
                  setCountryCode(dialCode);
                  setCountryIso2(iso2);
                }}
                error={showPhoneError ? phoneValidation.error : null}
                disabled={submitting}
              />
            </div>
          </>
        ) : (
          <>
            <div className="onboarding-field">
              <Label htmlFor="industry" className={FIELD_LABEL_CLASS}>
                What best describes your business?
              </Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger id="industry" className="warmup-field">
                  <SelectValue placeholder="Choose an option" />
                </SelectTrigger>
                <SelectContent className={FIELD_MENU_CLASS}>
                  {INDUSTRY_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="onboarding-field">
              <Label htmlFor="outreach-platform" className={FIELD_LABEL_CLASS}>
                Which platform do you use for your outreach?
              </Label>
              <OutreachPlatformField
                options={OUTREACH_PLATFORM_OPTIONS}
                value={outreachPlatform}
                onChange={setOutreachPlatform}
                disabled={submitting}
              />
            </div>

            <div className="onboarding-field">
              <Label htmlFor="referral-source" className={FIELD_LABEL_CLASS}>
                Where did you hear about us?
              </Label>
              <OutreachPlatformField
                id="referral-source"
                options={REFERRAL_SOURCE_OPTIONS}
                value={referralSource}
                onChange={setReferralSource}
                placeholder="Search or type where you heard about us"
                listLabel="Referral sources"
                disabled={submitting}
              />
            </div>
          </>
        )}
      </div>

      <div className="onboarding-foot">
        {step === 1 ? (
          <Button
            type="submit"
            size="lg"
            className="onboarding-continue w-full"
            disabled={!step1Done}
          >
            Next
            <ArrowRight aria-hidden />
          </Button>
        ) : (
          <div className="onboarding-foot-row">
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="onboarding-back"
              onClick={() => setStep(1)}
              disabled={submitting}
            >
              <ChevronLeft aria-hidden />
              Back
            </Button>
            <Button
              type="submit"
              size="lg"
              className="onboarding-continue flex-1"
              disabled={!canSubmit || submitting}
            >
              {submitting ? 'Saving…' : 'Continue'}
              <ArrowRight aria-hidden />
            </Button>
          </div>
        )}
        <p
          className={
            step === 2 && canSubmit ? 'onboarding-hint onboarding-hint-ready' : 'onboarding-hint'
          }
        >
          {step === 2 && canSubmit
            ? "All set. You're ready to go."
            : 'Fill in every field above to continue'}
        </p>
      </div>
    </form>
  );
}

const FIELD_LABEL_CLASS = 'text-[13px] font-semibold text-[color:var(--ink-700)]';
